// REUNIÃO GERAL (2026-09-03) — material técnico da semana.
// Sobe em 2 passos (upload_url → PUT no S3 → confirmar), o padrão da casa: o
// registro no banco só nasce DEPOIS que o arquivo chegou, senão um PUT que
// falha deixa linha órfã na tabela.
//
// 🚨 O Content-Type do PUT tem que ser EXATAMENTE o que o servidor assinou,
// senão o S3 responde SignatureDoesNotMatch. Por isso mandamos `content_type`
// no passo 1 e reusamos o valor que ele devolve no passo 2.

import { useState } from 'react';
import axios from 'axios';
import { App as AntApp, Button, Empty, Popconfirm, Space, Table, Typography, Upload } from 'antd';
import { DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { api, erroMsg, fmtDataHora, tamanhoLegivel } from './utils';
import { Anexo } from './types';

const { Text } = Typography;

interface Props {
  reuniaoId: string;
  anexos: Anexo[];
  podeGerenciar: boolean;
  onMudou: () => void;
}

export default function MaterialPanel({ reuniaoId, anexos, podeGerenciar, onMudou }: Props) {
  const { message } = AntApp.useApp();
  const [subindo, setSubindo] = useState(false);
  const [baixando, setBaixando] = useState<string | null>(null);

  async function subir(file: File) {
    setSubindo(true);
    try {
      // Passo 1: a URL assinada. O tipo vai junto porque ele entra na assinatura.
      const tipo = file.type || 'application/octet-stream';
      const d = await api('op_pauta_anexo_upload_url', {
        id: reuniaoId, filename: file.name, content_type: tipo,
      });
      if (!d?.upload_url) throw new Error('não veio a URL de upload');
      // Passo 2: o PUT direto no S3, com o MESMO Content-Type que foi assinado.
      await axios.put(d.upload_url, file, {
        headers: { 'Content-Type': d.content_type || tipo },
      });
      // Passo 3: só agora o registro no banco.
      await api('op_pauta_anexo_confirmar', {
        id: reuniaoId, s3_key: d.s3_key, nome: file.name,
        mime: tipo, tamanho_bytes: file.size,
      });
      message.success('Material anexado.');
      onMudou();
    } catch (e: any) {
      message.error('Falha ao anexar: ' + erroMsg(e));
    } finally { setSubindo(false); }
  }

  async function baixar(anexo: Anexo) {
    setBaixando(anexo.id);
    try {
      const d = await api('op_pauta_anexo_download_url', { anexo_id: anexo.id });
      if (!d?.download_url) throw new Error('não veio a URL de download');
      window.open(d.download_url, '_blank', 'noopener');
    } catch (e: any) {
      message.error('Falha ao baixar: ' + erroMsg(e));
    } finally { setBaixando(null); }
  }

  async function excluir(anexo: Anexo) {
    try {
      await api('op_pauta_anexo_excluir', { anexo_id: anexo.id });
      message.success('Material removido da pauta.');
      onMudou();
    } catch (e: any) {
      message.error('Falha ao remover: ' + erroMsg(e));
    }
  }

  const colunas = [
    {
      title: 'Arquivo', dataIndex: 'nome', key: 'nome',
      render: (v: string, r: Anexo) => (
        <Button type="link" style={{ padding: 0, textAlign: 'left', whiteSpace: 'normal' }}
          loading={baixando === r.id} icon={<DownloadOutlined />} onClick={() => baixar(r)}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Tamanho', dataIndex: 'tamanho_bytes', key: 'tam', width: 110,
      responsive: ['sm' as const],
      render: (v: any) => <Text type="secondary">{tamanhoLegivel(v)}</Text>,
    },
    {
      title: 'Anexado', dataIndex: 'criado_em', key: 'criado_em', width: 170,
      responsive: ['md' as const],
      render: (v: string, r: Anexo) => (
        <Text type="secondary">{fmtDataHora(v)}{r.criado_por ? ` · ${r.criado_por}` : ''}</Text>
      ),
    },
    ...(podeGerenciar ? [{
      title: '', key: 'acoes', width: 60,
      render: (_: any, r: Anexo) => (
        <Popconfirm title="Remover este material da pauta?" okText="Remover" cancelText="Não"
          onConfirm={() => excluir(r)}>
          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {podeGerenciar && (
        <Upload
          multiple={false}
          showUploadList={false}
          fileList={[] as UploadFile[]}
          // beforeUpload devolvendo false: o antd não sobe nada por conta
          // própria — quem controla os 3 passos é a função `subir`.
          beforeUpload={(file) => { subir(file as unknown as File); return false; }}
        >
          <Button icon={<UploadOutlined />} loading={subindo}>Anexar material</Button>
        </Upload>
      )}
      {anexos.length === 0
        ? <Empty description={podeGerenciar
            ? 'Nenhum material anexado. Suba a apresentação do tema técnico aqui.'
            : 'Nenhum material anexado nesta semana.'} />
        : <Table<Anexo> rowKey="id" size="small" columns={colunas} dataSource={anexos}
            pagination={false} tableLayout="fixed" />}
    </Space>
  );
}
